// CLI v0.8 — `cloudgrid dev`. Per decision 015 §"Multi-service dev runner"
// and CLI UX spec v17 §10. Three modes:
//
//   cloudgrid dev                    → multi-service mode (walk services:,
//                                       spawn each with sibling URLs).
//   cloudgrid dev <service-name>     → single-service mode (spawn just one).
//   cloudgrid dev -- <command>       → escape hatch (preserved verbatim).
//
// The runner gates on `.cloudgrid/link.json` (decision 012). Tunnels open
// through the API-WebSocket-relay (decision 015 + dev-tunnels.ts) — no
// kubectl. Identity stays out of env vars (decision 014).
//
// Crash semantics: a child exiting non-zero on its own does NOT kill its
// siblings. SIGINT / SIGTERM cleanly drain children + tunnels and exit 0.

import { Command } from 'commander';
import inquirer from 'inquirer';
import {
  spawn as defaultSpawn,
  spawnSync as defaultSpawnSync,
  type ChildProcess,
} from 'node:child_process';
import { createInterface } from 'node:readline';
import {
  existsSync,
  mkdirSync,
  readFileSync,
  unlinkSync,
  writeFileSync,
} from 'node:fs';
import { basename, dirname, join } from 'node:path';
import { parse as parseYaml } from 'yaml';
import chalk from 'chalk';
import treeKill from 'tree-kill';
import { hasCredentials } from '../credentials.js';
import { tryGetActiveOrg } from '../active-org.js';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { loadConfig } from '../config.js';
import { handleV2Error } from '../handle-error.js';
import type { DataMode, DevPolicy, DevSandbox, DevSeedResult } from '../../../shared/src/types.js';
import { requireLink, validateLinkVsActiveOrg, type LinkArtifact } from '../services/link.js';
import {
  startTunnels,
  redactDbUrl,
  type DevTunnels,
  type TunnelEnvVars,
} from '../services/dev-tunnels.js';
import {
  findFreePort as defaultFindFreePort,
  type FindFreePortOptions,
} from '../services/dev-port-allocator.js';
import { defaultPortForServiceType } from '../services/dev-default-ports.js';
import { CloudGridError, upperSnake } from '../utils.js';
import { isHttpModeCron } from '../services/cron-mode.js';
import {
  findParentLockfiles,
  formatParentLockfileWarning,
} from '../services/parent-lockfile-check.js';
import {
  detectVectorCapability,
  VECTOR_PGVECTOR_ENV_VAR,
} from '../services/vector-capability.js';
import {
  isDockerAvailable,
  startPgvectorSandbox,
  stopPgvectorSandbox,
  PGVECTOR_IMAGE,
  PGVECTOR_CONTAINER_PORT,
} from '../services/dev-pgvector-sandbox.js';
// BL621 Phase 3 (Decision 042 Mode 1 §dev "Auto-runs init if needed") — when a
// builder runs `cloudgrid dev` in an existing app with no cloudgrid.yaml, run
// gridify to prepare one before bailing out.
import { runGridify } from '../services/gridify.js';

// ── Types ─────────────────────────────────────────────────────────────────────

type ServiceType = 'node' | 'nextjs' | 'python' | 'static' | 'cron';

interface ServiceBlock {
  type: ServiceType;
  lang?: 'javascript' | 'typescript';
  schedule?: string;
  /** `'job'` (default) or an `http://` / `https://` URL — only valid for `cron`. */
  run?: string;
  env?: Record<string, string>;
}

interface CloudGridYamlShape {
  name?: string;
  services?: Record<string, ServiceBlock>;
}

/**
 * Per-service color cycle. Same six-color palette v0.7 used so users
 * with mixed v0.7 / v0.8 muscle memory see the same visual grouping.
 */
const SERVICE_COLORS = [
  chalk.cyan,
  chalk.magenta,
  chalk.yellow,
  chalk.green,
  chalk.blue,
  chalk.red,
];

const LOCK_FILE = '.cloudgrid-dev.lock';

interface LockFileShape {
  pid: number;
  started_at: string;
  services: Record<string, { port: number; child_pid: number | null }>;
}

interface SpawnedChild {
  name: string;
  child: ChildProcess;
}

// ── Public deps for testability ───────────────────────────────────────────────

type SpawnFn = typeof defaultSpawn;
type SpawnSyncFn = typeof defaultSpawnSync;
type FindFreePortFn = (hint?: number, opts?: FindFreePortOptions) => Promise<number>;

export interface RunDevDeps {
  client?: IV2Client;
  /** Override for tests — defaults to `child_process.spawn`. */
  spawn?: SpawnFn;
  /** Override for tests — defaults to `child_process.spawnSync` (auto-`npm install`). */
  spawnSync?: SpawnSyncFn;
  /** Override for tests — defaults to `startTunnels` from the relay path. */
  startTunnels?: typeof startTunnels;
  /** Override for tests — defaults to {@link defaultFindFreePort} (096 layer 1). */
  findFreePort?: FindFreePortFn;
  /** Override for tests — defaults to {@link runGridify} (dev first-run auto-init). */
  gridify?: typeof runGridify;
}

export interface RunDevOpts {
  /** Pass-through escape hatch — when set, the runner spawns one child with this command. */
  userCommand?: string[];
  /** Single-service mode — spawn just `<name>` from the yaml. */
  serviceName?: string;
  /** Force-overwrite an existing `.cloudgrid-dev.lock`. */
  force?: boolean;
  /** Refuse to fall back to ephemeral when the previous port is taken (096 layer 1). */
  fixedPorts?: boolean;
  /** Print current dev-session lock contents and exit. Read-only (096 layer 3). */
  status?: boolean;
  /** Kill any dev-session children recorded in the lock and remove the lock (096 layer 3). */
  kill?: boolean;
}

// ── Lock file ────────────────────────────────────────────────────────────────

function readLockSafe(path: string): LockFileShape | null {
  if (!existsSync(path)) return null;
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8'));
    if (parsed && typeof parsed.pid === 'number') return parsed as LockFileShape;
  } catch { /* fall through */ }
  return null;
}

function isPidAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

async function checkAndPrepareLock(cwd: string, force: boolean): Promise<void> {
  const lockPath = join(cwd, LOCK_FILE);
  const existing = readLockSafe(lockPath);
  if (!existing) return;
  if (!force && isPidAlive(existing.pid)) {
    throw new CloudGridError(
      `cloudgrid dev is already running (pid ${existing.pid}, started ${existing.started_at}). Stop it or use --force to override.`,
    );
  }
  // Stale parent — kill any child PIDs that are still alive before
  // overwriting (096 layer 2). This catches the kill-9 / crashed-terminal
  // case where the parent is gone but its children leak into the next run.
  await killOrphansFromLock(existing);
}

/**
 * Send SIGTERM to any service child PID in the lock that is still alive,
 * using `tree-kill` so the whole process subtree (e.g. `npx` → `tsx` →
 * webpack / tsc workers, `npx` → `next dev` → forked compilers) is reaped.
 * A plain `process.kill(pid, 'SIGTERM')` against the recorded `npx` parent
 * leaves long-lived grandchildren attached to the dev ports.
 *
 * Best-effort — we tolerate per-PID errors (subtree already exited, or
 * the OS refused the signal); a stale-lock cleanup hiccup must never
 * fail the new dev startup.
 */
async function killOrphansFromLock(lock: LockFileShape): Promise<void> {
  const orphans: Array<{ name: string; pid: number }> = [];
  for (const [name, svc] of Object.entries(lock.services ?? {})) {
    if (svc && typeof svc.child_pid === 'number' && isPidAlive(svc.child_pid)) {
      orphans.push({ name, pid: svc.child_pid });
    }
  }
  if (orphans.length === 0) return;
  process.stdout.write(
    `Found stale .cloudgrid-dev.lock from pid ${lock.pid} (not alive). Killing orphan service processes from previous run:\n`,
  );
  for (const { name, pid } of orphans) {
    const err = await new Promise<Error | undefined>((resolve) => {
      treeKill(pid, 'SIGTERM', (e) => resolve(e ?? undefined));
    });
    if (err) {
      // Subprocess subtree may have already exited; that's fine. Don't fail
      // the dev startup over a stale-lock cleanup hiccup.
      process.stdout.write(`  - ${name} (pid ${pid}) — kill failed: ${err.message}\n`);
    } else {
      process.stdout.write(`  - ${name} (pid ${pid}) — killed\n`);
    }
  }
  process.stdout.write('Starting fresh.\n');
}

/**
 * Pull per-service port hints out of a previous lock so the allocator
 * can offer them on the next start (096 layer 1). Returns an empty
 * map if the lock is missing or has no services.
 */
function readPreviousPorts(cwd: string): Record<string, number> {
  const lock = readLockSafe(join(cwd, LOCK_FILE));
  if (!lock) return {};
  const out: Record<string, number> = {};
  for (const [name, svc] of Object.entries(lock.services ?? {})) {
    if (svc && typeof svc.port === 'number' && svc.port > 0) {
      out[name] = svc.port;
    }
  }
  return out;
}

function writeLock(cwd: string, ports: Record<string, number>): void {
  const lockPath = join(cwd, LOCK_FILE);
  const body: LockFileShape = {
    pid: process.pid,
    started_at: new Date().toISOString(),
    services: Object.fromEntries(
      Object.entries(ports).map(([name, port]) => [name, { port, child_pid: null }]),
    ),
  };
  writeFileSync(lockPath, JSON.stringify(body, null, 2));
}

function updateLockChildPid(cwd: string, name: string, pid: number | null): void {
  const lockPath = join(cwd, LOCK_FILE);
  const existing = readLockSafe(lockPath);
  if (!existing) return;
  if (existing.services[name]) {
    existing.services[name].child_pid = pid;
    writeFileSync(lockPath, JSON.stringify(existing, null, 2));
  }
}

function removeLock(cwd: string): void {
  const lockPath = join(cwd, LOCK_FILE);
  try { unlinkSync(lockPath); } catch { /* ignore */ }
}

// ── --status / --kill verbs (096 layer 3) ────────────────────────────────────

/**
 * Read-only inspection of `.cloudgrid-dev.lock`. Prints a concise summary
 * (per-service port + child PID + alive/dead). When no lock is present
 * prints "No dev session is running." Never throws.
 */
function printDevStatus(cwd: string): void {
  const lock = readLockSafe(join(cwd, LOCK_FILE));
  if (!lock) {
    process.stdout.write('No dev session is running.\n');
    return;
  }
  const parentAlive = isPidAlive(lock.pid);
  process.stdout.write(
    `cloudgrid dev session — pid ${lock.pid} (${parentAlive ? 'alive' : 'not alive'}), started ${lock.started_at}\n`,
  );
  const entries = Object.entries(lock.services ?? {});
  if (entries.length === 0) {
    process.stdout.write('  (no services recorded)\n');
    return;
  }
  for (const [name, svc] of entries) {
    const port = typeof svc.port === 'number' ? svc.port : '?';
    const pid = typeof svc.child_pid === 'number' ? svc.child_pid : null;
    if (pid === null) {
      process.stdout.write(`  ${name}  port=${port}  pid=(unspawned)\n`);
    } else {
      const alive = isPidAlive(pid) ? 'alive' : 'not alive';
      process.stdout.write(`  ${name}  port=${port}  pid=${pid} (${alive})\n`);
    }
  }
}

/**
 * Explicit cleanup. Used when the standard SIGINT/SIGTERM cleanup didn't
 * run — e.g., the terminal was closed or `kill -9` hit the parent.
 * Idempotent: running it twice is safe.
 *
 * Sequence:
 *   1. If the parent runner PID is still alive, SIGTERM it and poll up to
 *      2s for graceful exit. The parent's own cleanup() drops tunnels and
 *      reaps children with SIGKILL escalation, so it is the right thing
 *      to wake first. (A previous version only signalled the recorded
 *      child PIDs, which left the parent's WebSocket tunnels to the relay
 *      open.) SIGKILL the parent after the 2s grace if it ignored SIGTERM.
 *   2. If the parent is already dead (or the SIGTERM call threw), fall
 *      through to per-child SIGTERMs as a fallback. The fallback can't
 *      drop tunnels — only the parent owns them — but it still tidies up
 *      orphan service processes that the parent might have leaked.
 */
async function killDevSession(cwd: string): Promise<void> {
  const lockPath = join(cwd, LOCK_FILE);
  const lock = readLockSafe(lockPath);
  if (!lock) {
    process.stdout.write('Nothing to clean up. No dev session lock present.\n');
    return;
  }

  // ── 1. Parent-first path: wake the runner so its own cleanup() runs.
  if (typeof lock.pid === 'number' && isPidAlive(lock.pid)) {
    let parentSignalled = false;
    try {
      process.kill(lock.pid, 'SIGTERM');
      parentSignalled = true;
      process.stdout.write(`Signalling parent runner (pid ${lock.pid})...\n`);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      process.stdout.write(`Parent runner (pid ${lock.pid}) — SIGTERM failed: ${detail}\n`);
      // Fall through to per-child cleanup below.
    }
    if (parentSignalled) {
      // Poll-wait up to 2s for the parent to exit gracefully. Its own
      // cleanup() will close tunnels and kill children with SIGKILL
      // escalation, then remove the lock.
      const start = Date.now();
      while (Date.now() - start < 2000 && isPidAlive(lock.pid)) {
        await new Promise((r) => setTimeout(r, 50));
      }
      if (isPidAlive(lock.pid)) {
        try {
          process.kill(lock.pid, 'SIGKILL');
          process.stdout.write(`Parent runner (pid ${lock.pid}) — SIGKILL after 2s grace\n`);
        } catch { /* already dead — fine */ }
      } else {
        process.stdout.write(`Parent runner (pid ${lock.pid}) — exited cleanly\n`);
      }
      // The parent's cleanup() already removed the lock and signalled
      // children. Don't double-kill or double-write.
      return;
    }
  }

  // ── 2. Fallback: parent already dead or unreachable. Tidy up the
  // orphaned children directly. Tunnels can't be closed here — only the
  // (now-dead) parent owned the WebSocket connections.
  let killed = 0;
  for (const [name, svc] of Object.entries(lock.services ?? {})) {
    if (svc && typeof svc.child_pid === 'number') {
      const pid = svc.child_pid;
      try {
        process.kill(pid, 'SIGTERM');
        killed++;
        process.stdout.write(`  ${name} (pid ${pid}) — SIGTERM sent\n`);
      } catch (err) {
        const detail = err instanceof Error ? err.message : String(err);
        process.stdout.write(`  ${name} (pid ${pid}) — kill failed: ${detail}\n`);
      }
    }
  }
  removeLock(cwd);
  if (killed > 0) {
    process.stdout.write(`Cleaned up ${killed} child process(es) and removed the lock.\n`);
  } else {
    process.stdout.write('Lock removed. No child processes were recorded.\n');
  }
}

// ── Yaml loader ──────────────────────────────────────────────────────────────

function loadYaml(cwd: string): CloudGridYamlShape {
  const yamlPath = join(cwd, 'cloudgrid.yaml');
  if (!existsSync(yamlPath)) {
    throw new CloudGridError(
      `No cloudgrid.yaml found at ${yamlPath}. Run: cloudgrid init <kind> <name>`,
    );
  }
  let parsed: unknown;
  try {
    parsed = parseYaml(readFileSync(yamlPath, 'utf-8'));
  } catch (err) {
    throw new CloudGridError(
      `Could not parse cloudgrid.yaml: ${(err as Error).message}`,
    );
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new CloudGridError('cloudgrid.yaml is malformed (expected an object).');
  }
  return parsed as CloudGridYamlShape;
}

/**
 * Validate that every service block points at an existing folder under
 * `services/<name>/`. Folders without a corresponding service block are
 * tolerated — the user may have folders the platform doesn't deploy.
 */
function validateServiceFolders(cwd: string, services: Record<string, ServiceBlock>): void {
  for (const [name, svc] of Object.entries(services)) {
    // Backlog 076-C: HTTP-mode cron has no source folder by design.
    if (isHttpModeCron(svc)) continue;
    const dir = join(cwd, 'services', name);
    if (!existsSync(dir)) {
      throw new CloudGridError(
        `Service '${name}' is declared in cloudgrid.yaml but services/${name}/ does not exist. Run: cloudgrid init service ${name} --type ${svc.type}`,
      );
    }
  }
}

// ── Entity-slug fallback chain (closes R1 from the research artifact) ───────

/** Resolve `{app-name}` per decision 015 §"Identity-aware behavior". */
function resolveEntitySlug(
  link: { entity_slug?: string } | null,
  yaml: CloudGridYamlShape,
  cwd: string,
): string {
  // `link.entity_slug` is the canonical source — written by `cloudgrid
  // init` and `cloudgrid plug --existing`. Pre-existing link.json files
  // (from before the field landed) fall through to yaml `name:` then
  // `basename(cwd)`. All three layers stay in place so a tunnel keeps
  // working through any single missing field.
  if (link && typeof link.entity_slug === 'string' && link.entity_slug !== '') {
    return link.entity_slug;
  }
  if (typeof yaml.name === 'string' && yaml.name !== '') return yaml.name;
  return basename(cwd);
}

// ── Auto-install ─────────────────────────────────────────────────────────────

/**
 * Run `npm install` (or `npm ci` if a lockfile exists) for `node` /
 * `nextjs` services that lack `node_modules/`. Prints a concise per-service
 * line; on failure throws a CloudGridError naming the service.
 */
function autoInstall(
  cwd: string,
  spawnSync: SpawnSyncFn,
  services: Record<string, ServiceBlock>,
): void {
  for (const [name, svc] of Object.entries(services)) {
    if (svc.type !== 'node' && svc.type !== 'nextjs') continue;
    const svcDir = join(cwd, 'services', name);
    const modulesDir = join(svcDir, 'node_modules');
    if (existsSync(modulesDir)) continue;
    const lockPresent = existsSync(join(svcDir, 'package-lock.json'));
    const cmd = lockPresent ? 'ci' : 'install';
    process.stdout.write(`Installing dependencies for ${name}...\n`);
    const result = spawnSync('npm', [cmd], { cwd: svcDir, stdio: 'inherit' });
    if (result.status !== 0) {
      throw new CloudGridError(
        `npm ${cmd} failed for service '${name}'. Fix the issue and retry.`,
      );
    }
  }
}

// ── Per-type runners ─────────────────────────────────────────────────────────

interface RunnerCommand {
  cmd: string;
  args: string[];
}

function runnerForService(svc: ServiceBlock, port: number): RunnerCommand | null {
  switch (svc.type) {
    case 'node':
      return svc.lang === 'typescript'
        ? { cmd: 'npx', args: ['tsx', 'watch', 'src/index.ts'] }
        : { cmd: 'node', args: ['--watch', 'src/index.js'] };
    case 'nextjs':
      return { cmd: 'npx', args: ['next', 'dev', '-p', String(port)] };
    case 'python':
      return { cmd: 'python', args: ['src/main.py'] };
    case 'static':
      // Backlog 303 — serve the service root, where index.html lives and
      // where the deployer (no-build static COPYs `.` into nginx) serves
      // from. Keeps local dev consistent with the deployed surface.
      return { cmd: 'npx', args: ['serve', '.', '-l', String(port)] };
    case 'cron':
      // cron services are handled separately — they do not have a long-
      // running runner.
      return null;
    default:
      return null;
  }
}

/**
 * Compose per-service env vars: tunnel vars + sibling URLs + `PORT` +
 * `SERVICE_NAME` / `APP_NAME`. Identity vars (CLOUDGRID_*) intentionally
 * not added (decision 014).
 */
function buildServiceEnv(opts: {
  serviceName: string;
  serviceType: ServiceType;
  port: number;
  ports: Record<string, number>;
  services: Record<string, ServiceBlock>;
  tunnels: TunnelEnvVars;
  entitySlug: string;
  serviceLevelEnv?: Record<string, string>;
  /** BL579 M4 — local pgvector sandbox URL, injected as VECTOR_PGVECTOR_URL. */
  vectorUrl?: string;
}): Record<string, string> {
  const env: Record<string, string> = { ...opts.tunnels };

  env.PORT = String(opts.port);
  env.SERVICE_NAME = opts.serviceName;
  env.APP_NAME = opts.entitySlug;

  if (opts.serviceType === 'node' || opts.serviceType === 'nextjs') {
    env.NODE_ENV = 'development';
  }

  // Sibling URLs — every other non-cron service.
  for (const [other, otherSvc] of Object.entries(opts.services)) {
    if (other === opts.serviceName) continue;
    if (otherSvc.type === 'cron') continue;
    const otherPort = opts.ports[other];
    if (typeof otherPort !== 'number') continue;
    env[`${upperSnake(other)}_URL`] = `http://localhost:${otherPort}`;
  }

  // BL579 M4 — the managed vector substrate. Mirrors the prod env-var name so
  // the app's code reads the same key locally as deployed.
  if (opts.vectorUrl) {
    env[VECTOR_PGVECTOR_ENV_VAR] = opts.vectorUrl;
  }

  // Service-level user env (lowest precedence — overridable via runtime, but
  // we set them here so they're visible to the child). Place before the
  // standard vars so user-set NODE_ENV doesn't override development mode.
  if (opts.serviceLevelEnv) {
    for (const [k, v] of Object.entries(opts.serviceLevelEnv)) {
      // User explicit assignment wins over runner default — intentional.
      env[k] = v;
    }
  }

  return env;
}

// ── Output prefixing ─────────────────────────────────────────────────────────

function prefixStream(
  stream: NodeJS.ReadableStream,
  name: string,
  color: typeof chalk.cyan,
  out: NodeJS.WritableStream,
): void {
  const rl = createInterface({ input: stream });
  rl.on('line', (line) => {
    out.write(`${color(`[${name}]`)} ${line}\n`);
  });
}

// ── Main runner ──────────────────────────────────────────────────────────────

export async function runDev(opts: RunDevOpts, deps: RunDevDeps = {}): Promise<void> {
  const cwd = process.cwd();

  // Inspection / cleanup verbs — read-only and lock-only. They run before
  // requireLink so the user can clean up a directory whose link.json was
  // never written or has gone stale.
  if (opts.status) {
    printDevStatus(cwd);
    return;
  }
  if (opts.kill) {
    await killDevSession(cwd);
    return;
  }

  // Decision 042 Mode 1 §dev — auto-run init (gridify) on first invocation when
  // the folder has no cloudgrid.yaml. Skipped for the escape hatch
  // (`cloudgrid dev -- <cmd>`), where the user explicitly drives their own
  // command and a surprise yaml write would be wrong. Gridify only prepares the
  // yaml; it never registers a server entity, so there is still no link to run
  // against — we guide the user to `plug` and stop here. (Plug reads the yaml,
  // registers the entity, and deploys; dev then has a linked entity to inject
  // grid resources into.)
  const hasUserCommand = !!opts.userCommand && opts.userCommand.length > 0;
  if (!hasUserCommand && !existsSync(join(cwd, 'cloudgrid.yaml'))) {
    const gridify = deps.gridify ?? runGridify;
    const result = await gridify(cwd, {}, {});
    if (result.kind === 'undetected') {
      throw new CloudGridError(
        [
          'No cloudgrid.yaml here, and I could not detect an app to set up.',
          'Run: cloudgrid init <app|agent> <slug>',
        ].join('\n'),
      );
    }
    if (result.kind === 'created') {
      process.stdout.write(
        'Run cloudgrid plug to register and deploy, then cloudgrid dev.\n',
      );
    }
    return;
  }

  const link = requireLink();

  // Login warning (decision 015 §"Identity-aware behavior", closes R3).
  // Non-blocking — the message prints, the runner proceeds. MUST run before
  // getV2Client() since that throws AUTH_REQUIRED on missing credentials and
  // would otherwise eclipse the warning entirely.
  if (!hasCredentials()) {
    process.stdout.write(
      'Not signed in. AI features will not work in dev. Run: cloudgrid login\n',
    );
  }

  // Soft active-org check (skip when no slug locally, mirrors prior code path).
  const client = deps.client ?? (await getV2Client());
  const activeOrg = await tryGetActiveOrg(client);
  if (activeOrg !== null) {
    validateLinkVsActiveOrg(link, activeOrg);
  }

  const spawnFn = deps.spawn ?? defaultSpawn;
  const spawnSyncFn = deps.spawnSync ?? defaultSpawnSync;
  const startTunnelsFn = deps.startTunnels ?? startTunnels;
  const findFreePortFn: FindFreePortFn = deps.findFreePort ?? defaultFindFreePort;

  // ── Escape hatch: cloudgrid dev -- <cmd> (preserved verbatim) ──────────
  if (opts.userCommand && opts.userCommand.length > 0) {
    return runEscapeHatch({
      cwd,
      link,
      client,
      userCommand: opts.userCommand,
      spawnFn,
      startTunnelsFn,
    });
  }

  // ── Multi/single-service mode ───────────────────────────────────────────
  const yaml = loadYaml(cwd);
  const services = yaml.services ?? {};
  if (Object.keys(services).length === 0) {
    throw new CloudGridError(
      'cloudgrid.yaml has no services. Run: cloudgrid init service <name> --type <type>',
    );
  }

  // Single-service mode picks one out of the yaml.
  if (opts.serviceName) {
    if (!services[opts.serviceName]) {
      throw new CloudGridError(
        `Service '${opts.serviceName}' is not declared in cloudgrid.yaml. Available: ${Object.keys(services).join(', ')}`,
      );
    }
  }

  validateServiceFolders(cwd, services);

  // Capture previous lock's per-service ports BEFORE the lock-prepare step
  // overwrites the file, so each new allocation can reuse the previous port
  // (096 layer 1).
  const previousPorts = readPreviousPorts(cwd);

  // Lock file gate.
  await checkAndPrepareLock(cwd, opts.force ?? false);

  // Parent-of-project lockfile sanity check (backlog 353). Defensive only —
  // warns but never blocks. Skip when no node/nextjs service is involved
  // since the warning is specific to Next.js workspace-root mis-detection
  // (the bug class doesn't apply to python / static).
  const hasNodeService = Object.values(services).some(
    (s) => s.type === 'node' || s.type === 'nextjs',
  );
  if (hasNodeService) {
    const stray = findParentLockfiles(cwd);
    if (stray.length > 0) {
      process.stdout.write(formatParentLockfileWarning(stray));
    }
  }

  // Auto-install for node/nextjs (per matrix in decision 015).
  autoInstall(cwd, spawnSyncFn, services);

  // Allocate ports — only for non-cron services. Pass the previous port
  // (when present) as a hint so dev restarts keep stable URLs.
  const ports: Record<string, number> = {};
  for (const [name, svc] of Object.entries(services)) {
    if (svc.type === 'cron') continue;
    const hint = previousPorts[name];
    // No prior lock? Use the convention default for the service type
    // (nextjs/node → 3000, python → 5000, static → 8080). The allocator
    // linear-scans +1..+20 from the default, then falls through to
    // ephemeral. Convention default never interacts with --fixed-ports.
    const defaultHint =
      hint === undefined ? defaultPortForServiceType(svc.type) ?? undefined : undefined;
    ports[name] = await findFreePortFn(hint, {
      fixed: opts.fixedPorts ?? false,
      defaultHint,
    });
  }

  // Open tunnels through the relay.
  const config = loadConfig();
  const entitySlug = resolveEntitySlug(link as { entity_slug?: string }, yaml, cwd);
  const tunnels: DevTunnels = await startTunnelsFn(client, entitySlug, config.api_url, {
    orgSlug: link.org_slug,
    entityId: link.entity_id,
  });

  writeLock(cwd, ports);

  // ── BL579 M4 — local pgvector sandbox ──────────────────────────────────
  // When the app declares the managed vector substrate (needs.vector /
  // requires.vector), spin a local pgvector container and inject
  // VECTOR_PGVECTOR_URL so the app's code connects in dev exactly as in prod.
  // Every failure is SOFT: a missing Docker daemon or a container that won't
  // start prints a friendly notice and `cloudgrid dev` continues without the
  // env var. The container is torn down by cleanup() on SIGINT/SIGTERM.
  let vectorUrl: string | undefined;
  let vectorContainer: string | undefined;
  if (detectVectorCapability(yaml)) {
    if (!isDockerAvailable(spawnSyncFn)) {
      process.stdout.write(
        'Docker is not running — pgvector sandbox skipped. ' +
          'The app will start without VECTOR_PGVECTOR_URL.\n',
      );
    } else {
      const vectorPort = await findFreePortFn(PGVECTOR_CONTAINER_PORT);
      process.stdout.write(
        `Starting local pgvector (${PGVECTOR_IMAGE}) on port ${vectorPort}...\n`,
      );
      const sandbox = await startPgvectorSandbox({
        appSlug: entitySlug,
        port: vectorPort,
        spawnSync: spawnSyncFn,
      });
      if (sandbox) {
        vectorUrl = sandbox.url;
        vectorContainer = sandbox.containerName;
      } else {
        process.stdout.write(
          `pgvector sandbox failed to start — continuing without VECTOR_PGVECTOR_URL. ` +
            `Check that Docker can pull ${PGVECTOR_IMAGE}.\n`,
        );
      }
    }
  }

  // Output banner. MONGODB_URL / REDIS_URL are redacted — post-BL449 the dev
  // MONGODB_URL, and post-M4 the dev REDIS_URL, each carry a per-org password
  // (injected via tunnels.envVars from resolveDevDbTemplates), which must never
  // reach the banner. `redactDbUrl` strips the userinfo for display only; the
  // child process still receives the real credentialed URL via env.
  process.stdout.write(`Starting ${entitySlug} locally...\n`);
  process.stdout.write(`  Linked entity: ${link.entity_id} (${link.org_slug})\n`);
  process.stdout.write(`  Injecting:\n`);
  process.stdout.write(`    MONGODB_URL     → ${redactDbUrl(tunnels.envVars.MONGODB_URL)}\n`);
  process.stdout.write(`    REDIS_URL       → ${redactDbUrl(tunnels.envVars.REDIS_URL)}\n`);
  process.stdout.write(`    N8N_WEBHOOK_URL → ${tunnels.envVars.N8N_WEBHOOK_URL}\n`);
  process.stdout.write(`    AI_GATEWAY_URL  → ${tunnels.envVars.AI_GATEWAY_URL}\n`);
  if (vectorUrl) {
    // Redact the (local-only) password for display; the child still gets the
    // real credentialed URL via env.
    process.stdout.write(`    VECTOR_PGVECTOR_URL → ${redactDbUrl(vectorUrl)}\n`);
  }
  process.stdout.write(`\n`);

  // Decide which services to spawn.
  const targetNames = opts.serviceName ? [opts.serviceName] : Object.keys(services);

  // Print URLs (non-cron services).
  for (let i = 0; i < targetNames.length; i++) {
    const name = targetNames[i];
    const svc = services[name];
    if (svc.type === 'cron') continue;
    const color = SERVICE_COLORS[i % SERVICE_COLORS.length];
    process.stdout.write(`  ${color(name)}: http://localhost:${ports[name]}\n`);
  }
  process.stdout.write(`\n  Press Ctrl-C to stop.\n\n`);

  // Single-service mode warning — sibling URLs are still injected so the
  // child can call the (currently absent) other services if/when they're
  // started in another window.
  if (opts.serviceName && Object.keys(services).length > 1) {
    process.stdout.write(
      `Single-service mode: only '${opts.serviceName}' is starting. Sibling <SVC>_URL env vars still point at planned ports.\n\n`,
    );
  }

  const spawned: SpawnedChild[] = [];
  let stopping = false;

  // shutdownDone resolves when cleanup() finishes. The runner's tail awaits
  // it instead of hanging forever — that lets tests trigger SIGINT and have
  // `runDev` actually resolve, while production keeps the same observable
  // shape because `process.exit(0)` is the last call inside cleanup().
  let resolveShutdown!: () => void;
  const shutdownDone = new Promise<void>((r) => { resolveShutdown = r; });

  // Cleanup once — used by both SIGINT and SIGTERM handlers.
  const cleanup = async (): Promise<void> => {
    if (stopping) return;
    stopping = true;
    process.stdout.write('\nStopping...\n');
    // SIGTERM all children; force-kill after STOP_GRACEFUL_TIMEOUT_MS.
    const STOP_GRACEFUL_TIMEOUT_MS = 2_000;
    await Promise.all(
      spawned.map(
        ({ child }) =>
          new Promise<void>((resolve) => {
            if (child.exitCode !== null || child.signalCode !== null) {
              resolve();
              return;
            }
            try { child.kill('SIGTERM'); } catch { /* ignore */ }
            const timer = setTimeout(() => {
              try { child.kill('SIGKILL'); } catch { /* ignore */ }
              resolve();
            }, STOP_GRACEFUL_TIMEOUT_MS);
            child.once('exit', () => {
              clearTimeout(timer);
              resolve();
            });
          }),
      ),
    );
    await tunnels.stop();
    // BL579 M4 — tear down the local pgvector container (started with --rm, so
    // `docker rm -f` both stops and removes it). Best-effort.
    if (vectorContainer) stopPgvectorSandbox(vectorContainer, spawnSyncFn);
    removeLock(cwd);
    resolveShutdown();
    process.exit(0);
  };

  process.once('SIGINT', () => { void cleanup(); });
  process.once('SIGTERM', () => { void cleanup(); });

  // Spawn each non-cron service. Cron services run inline (job mode) or
  // print-and-skip (http mode) per the matrix.
  for (let i = 0; i < targetNames.length; i++) {
    const name = targetNames[i];
    const svc = services[name];
    const color = SERVICE_COLORS[i % SERVICE_COLORS.length];

    if (svc.type === 'cron') {
      // Backlog 076-C: HTTP-mode cron has no local process — print and skip.
      if (isHttpModeCron(svc)) {
        process.stdout.write(
          `${color(`[${name}]`)} Skipping HTTP-cron ${name}: scheduled call to ${svc.run} on '${svc.schedule ?? 'unset'}'\n`,
        );
        continue;
      }
      await runCronOnce(name, svc, services, ports, tunnels.envVars, entitySlug, cwd, color, spawnFn, vectorUrl);
      continue;
    }

    const runner = runnerForService(svc, ports[name]);
    if (!runner) {
      process.stdout.write(
        `${color(`[${name}]`)} unknown service type '${svc.type}' — skipping\n`,
      );
      continue;
    }

    const env = buildServiceEnv({
      serviceName: name,
      serviceType: svc.type,
      port: ports[name],
      ports,
      services,
      tunnels: tunnels.envVars,
      entitySlug,
      serviceLevelEnv: svc.env,
      vectorUrl,
    });

    const child = spawnFn(runner.cmd, runner.args, {
      cwd: join(cwd, 'services', name),
      env: { ...process.env, ...env },
      stdio: ['ignore', 'pipe', 'pipe'],
      shell: false,
    });

    if (child.stdout) prefixStream(child.stdout, name, color, process.stdout);
    if (child.stderr) prefixStream(child.stderr, name, color, process.stderr);

    if (child.pid !== undefined && child.pid !== null) {
      updateLockChildPid(cwd, name, child.pid);
    }

    // Don't crash on individual exits — print and continue.
    child.on('exit', (code, signal) => {
      if (stopping) return;
      const display = code !== null ? `exited ${code}` : `terminated ${signal ?? 'signal'}`;
      process.stdout.write(`${color(`[${name}]`)} ${display}\n`);
    });
    child.on('error', (err) => {
      process.stderr.write(`${color(`[${name}]`)} spawn error: ${err.message}\n`);
    });

    spawned.push({ name, child });
  }

  // Wait until cleanup runs (triggered by SIGINT / SIGTERM). In production
  // `process.exit(0)` ends the process; in tests where `process.exit` is
  // mocked, this awaits the completion of the async cleanup so the test
  // sees a clean resolution.
  await shutdownDone;
}

// ── Cron handling ────────────────────────────────────────────────────────────

/**
 * Per the matrix: `cron / run: job` runs once and prints the result.
 * `cron / run: http` prints a notice (production triggers it on schedule).
 */
async function runCronOnce(
  name: string,
  svc: ServiceBlock,
  services: Record<string, ServiceBlock>,
  ports: Record<string, number>,
  tunnels: TunnelEnvVars,
  entitySlug: string,
  cwd: string,
  color: typeof chalk.cyan,
  spawnFn: SpawnFn,
  vectorUrl?: string,
): Promise<void> {
  const runMode = svc.run ?? 'job';
  if (runMode !== 'job') {
    process.stdout.write(
      `${color(`[${name}]`)} cron run mode '${runMode}'. URL is hit on schedule '${svc.schedule ?? 'unset'}' in production. Skipping in dev.\n`,
    );
    return;
  }

  // Resolve language by inspecting the service folder per decision 015 §"Cron language resolution".
  const svcDir = join(cwd, 'services', name);
  const isNode = existsSync(join(svcDir, 'package.json'));
  const isPython = existsSync(join(svcDir, 'requirements.txt'));
  if (!isNode && !isPython) {
    throw new CloudGridError(
      `Service '${name}' is type 'cron' but has neither package.json nor requirements.txt — cannot resolve language (CRON_LANGUAGE_UNDETECTABLE).`,
    );
  }

  const env = buildServiceEnv({
    serviceName: name,
    serviceType: 'cron' as const,
    // cron has no port; child reading process.env.PORT gets a sentinel '0'.
    port: 0,
    ports,
    services,
    tunnels,
    entitySlug,
    serviceLevelEnv: svc.env,
    vectorUrl,
  });

  const cmd = isNode ? 'node' : 'python';
  const args = isNode ? ['src/job.js'] : ['src/main.py'];
  process.stdout.write(`${color(`[${name}]`)} Running cron job once...\n`);

  await new Promise<void>((resolve) => {
    const child = spawnFn(cmd, args, {
      cwd: svcDir,
      env: { ...process.env, ...env },
      stdio: ['ignore', 'pipe', 'pipe'],
      shell: false,
    });
    if (child.stdout) prefixStream(child.stdout, name, color, process.stdout);
    if (child.stderr) prefixStream(child.stderr, name, color, process.stderr);
    child.on('exit', (code) => {
      process.stdout.write(`${color(`[${name}]`)} cron exited ${code ?? 'null'}.\n`);
      resolve();
    });
    child.on('error', (err) => {
      process.stderr.write(`${color(`[${name}]`)} spawn error: ${err.message}\n`);
      resolve();
    });
  });
}

// ── Escape hatch (cloudgrid dev -- <cmd>) ────────────────────────────────────

interface EscapeHatchDeps {
  cwd: string;
  link: ReturnType<typeof requireLink>;
  client: IV2Client;
  userCommand: string[];
  spawnFn: SpawnFn;
  startTunnelsFn: typeof startTunnels;
}

async function runEscapeHatch(deps: EscapeHatchDeps): Promise<void> {
  const config = loadConfig();
  // For escape-hatch mode the entity-slug fallback is the same chain. We
  // load yaml best-effort — if it's missing, basename(cwd) is the slug.
  let yaml: CloudGridYamlShape = {};
  try { yaml = loadYaml(deps.cwd); } catch { /* tolerated */ }
  const entitySlug = resolveEntitySlug(
    deps.link as { entity_slug?: string },
    yaml,
    deps.cwd,
  );
  const tunnels: DevTunnels = await deps.startTunnelsFn(deps.client, entitySlug, config.api_url, {
    orgSlug: deps.link.org_slug,
    entityId: deps.link.entity_id,
  });

  process.stdout.write(`Starting ${entitySlug} locally...\n`);
  process.stdout.write(`  Linked entity: ${deps.link.entity_id} (${deps.link.org_slug})\n`);
  process.stdout.write(`  Injecting:\n`);
  process.stdout.write(`    MONGODB_URL     → ${redactDbUrl(tunnels.envVars.MONGODB_URL)}\n`);
  process.stdout.write(`    REDIS_URL       → ${redactDbUrl(tunnels.envVars.REDIS_URL)}\n`);
  process.stdout.write(`    N8N_WEBHOOK_URL → ${tunnels.envVars.N8N_WEBHOOK_URL}\n`);
  process.stdout.write(`    AI_GATEWAY_URL  → ${tunnels.envVars.AI_GATEWAY_URL}\n`);
  process.stdout.write(`  Tunnels live.\n\n  Press Ctrl-C to stop.\n`);

  let childProcess: ChildProcess | undefined;
  let stopped = false;
  let resolveShutdown!: () => void;
  const shutdownDone = new Promise<void>((resolve) => { resolveShutdown = resolve; });

  // Shared cleanup for SIGINT and SIGTERM. Previously only SIGINT was
  // handled, so `kill <pid>` on a `cloudgrid dev -- <cmd>` runner hung
  // forever (Node ignored the default SIGTERM and the runner's signal
  // listeners never fired). The 2s SIGKILL escalation mirrors the
  // multi-service `cleanup()` above — a child that ignores SIGTERM must
  // not block tunnel shutdown indefinitely.
  const ESCAPE_HATCH_GRACEFUL_TIMEOUT_MS = 2_000;
  const cleanupAndExit = async (): Promise<void> => {
    if (stopped) return;
    stopped = true;
    process.stdout.write('\nStopping...\n');
    if (childProcess) {
      try { childProcess.kill('SIGTERM'); } catch { /* ignore */ }
      const killTimer = setTimeout(() => {
        if (childProcess) {
          try { childProcess.kill('SIGKILL'); } catch { /* ignore */ }
        }
      }, ESCAPE_HATCH_GRACEFUL_TIMEOUT_MS);
      childProcess.once('exit', () => clearTimeout(killTimer));
    }
    await tunnels.stop();
    resolveShutdown();
    process.exit(0);
  };

  process.once('SIGINT', () => { void cleanupAndExit(); });
  process.once('SIGTERM', () => { void cleanupAndExit(); });

  const [cmd, ...args] = deps.userCommand;
  childProcess = deps.spawnFn(cmd, args, {
    env: { ...process.env, ...tunnels.envVars },
    stdio: 'inherit',
    shell: false,
  });

  const exitCode = await new Promise<number>((resolve) => {
    childProcess!.on('exit', (code) => resolve(code ?? 0));
  });

  // If SIGINT already fired, the cleanup handler is in-flight — let it
  // finish via shutdownDone. Otherwise the child exited on its own and
  // the runner should drop the tunnels and (on non-zero) propagate the code.
  if (stopped) {
    await shutdownDone;
    return;
  }
  await tunnels.stop();
  if (exitCode !== 0) {
    process.exit(exitCode);
  }
}

// ── Make sure mkdirSync is referenced — silence unused-import in some
// dev-only build configs. (mkdirSync used by the scaffold helper imports;
// included here for symmetry with v0.7's util layout.)
void mkdirSync;
void dirname;

// ── dev seed / dev reset (Dev Grid M1) ───────────────────────────────────────

interface DevSandboxResponse {
  sandbox: DevSandbox;
  seed: DevSeedResult;
}

/**
 * Resolve the canonical org slug from the link's `org_id`. `link.org_slug` is
 * display-only and may be stale (backlog 308), so prefer the slug the server
 * currently reports for the org; fall back to the stored slug if the lookup
 * fails.
 */
async function resolveOrgSlug(client: IV2Client, link: LinkArtifact): Promise<string> {
  try {
    const orgs = await client.getOrgs();
    const match = orgs.find((o) => o.org_id === link.org_id);
    if (match?.slug) return match.slug;
  } catch {
    /* fall through to the stored slug */
  }
  return link.org_slug;
}

function renderSeed(seed: DevSeedResult): void {
  const collections = seed.collections.length > 0 ? seed.collections.join(', ') : '(none)';
  const cost = typeof seed.cost_usd === 'number' ? ` · Cost: $${seed.cost_usd.toFixed(2)}` : '';
  process.stdout.write(
    `Mode: ${seed.mode} · Collections: ${collections} · Records: ${seed.records_written}${cost}\n`,
  );
  process.stdout.write('Ready. Connect with $DEV_URL.\n');
}

/**
 * Map a dev-sandbox API error to the seed exit-code contract (spec §CLI): mode
 * not available → 2, quota or cost cap → 3, snapshots disabled → 4, empty source
 * → 5, everything else (network, auth, other refusal) → 1 via the shared handler.
 */
async function exitForSandboxError(err: unknown): Promise<never> {
  if (err instanceof V2ApiError) {
    const byCode: Record<string, number> = {
      DEV_MODE_UNAVAILABLE: 2,
      DEV_SEED_QUOTA_EXCEEDED: 3,
      DEV_SNAPSHOTS_DISABLED: 4,
      DEV_SEED_EMPTY_SOURCE: 5,
    };
    const code = byCode[err.code];
    if (code !== undefined) {
      process.stderr.write(`${err.message}\n`);
      process.exit(code);
    }
  }
  return handleV2Error(err);
}

async function runDevSeed(opts: { from?: string; collections?: string; yes?: boolean }): Promise<void> {
  const link = requireLink();
  const client = await getV2Client();
  const slug = await resolveOrgSlug(client, link);

  const body: Record<string, unknown> = { entity_id: link.entity_id };
  if (opts.from) body.mode = opts.from as DataMode;
  if (opts.collections) {
    const names = opts.collections.split(',').map((s) => s.trim()).filter((s) => s !== '');
    if (names.length > 0) body.collections = names;
  }
  if (opts.yes) body.yes = true;

  try {
    const result = await client.post<DevSandboxResponse>(
      `/api/v2/grids/${encodeURIComponent(slug)}/dev-sandbox/seed`,
      body,
    );
    renderSeed(result.seed);
  } catch (err) {
    await exitForSandboxError(err);
  }
}

async function runDevReset(opts: { yes?: boolean }): Promise<void> {
  const link = requireLink();
  if (!opts.yes) {
    const { ok } = await inquirer.prompt<{ ok: boolean }>([
      {
        type: 'confirm',
        name: 'ok',
        message: 'Reset the sandbox? This drops all seeded collections and reseeds to the grid default.',
        default: false,
      },
    ]);
    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  const client = await getV2Client();
  const slug = await resolveOrgSlug(client, link);

  try {
    const result = await client.post<DevSandboxResponse>(
      `/api/v2/grids/${encodeURIComponent(slug)}/dev-sandbox/reset`,
      { entity_id: link.entity_id, yes: opts.yes ?? false },
    );
    renderSeed(result.seed);
  } catch (err) {
    await exitForSandboxError(err);
  }
}

// ── dev policy / dev policy set (Dev Grid M2) ────────────────────────────────

interface DevPolicyResponse {
  policy: DevPolicy;
}

/** Three-line policy summary (spec §M2 CLI surface). */
function renderPolicy(policy: DevPolicy): void {
  process.stdout.write(`Default mode: ${policy.default_mode}\n`);
  process.stdout.write(`Allowed: ${policy.allowed_modes.join(', ')}\n`);
  const snapshots = policy.snapshots_enabled ? 'enabled' : 'disabled — see /admin/platform';
  process.stdout.write(`Snapshots: ${snapshots}\n`);
}

/**
 * Map a policy API error to the M2 exit-code contract: a non-admin `policy set`
 * (ORG_ADMIN_REQUIRED) → exit 6; everything else (mode-not-allowed validation,
 * network, auth) → the shared handler's exit 1.
 */
async function exitForPolicyError(err: unknown): Promise<never> {
  if (err instanceof V2ApiError && err.code === 'ORG_ADMIN_REQUIRED') {
    process.stderr.write(`${err.message}\n`);
    process.exit(6);
  }
  return handleV2Error(err);
}

async function runDevPolicy(opts: { json?: boolean }): Promise<void> {
  const link = requireLink();
  const client = await getV2Client();
  const slug = await resolveOrgSlug(client, link);
  try {
    const result = await client.get<DevPolicyResponse>(
      `/api/v2/grids/${encodeURIComponent(slug)}/dev-sandbox/policy`,
    );
    if (opts.json) {
      process.stdout.write(`${JSON.stringify(result.policy, null, 2)}\n`);
      return;
    }
    renderPolicy(result.policy);
  } catch (err) {
    await exitForPolicyError(err);
  }
}

function parseAllowSnapshots(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (v === 'on' || v === 'true') return true;
  if (v === 'off' || v === 'false') return false;
  throw new CloudGridError("--allow-snapshots takes 'on' or 'off'.");
}

async function runDevPolicySet(opts: {
  defaultMode?: string;
  allowSnapshots?: string;
}): Promise<void> {
  const body: Record<string, unknown> = {};
  if (opts.defaultMode) body.default_mode = opts.defaultMode as DataMode;
  if (opts.allowSnapshots !== undefined) {
    body.allow_dev_snapshots = parseAllowSnapshots(opts.allowSnapshots);
  }
  if (Object.keys(body).length === 0) {
    throw new CloudGridError('Set at least one of --default-mode or --allow-snapshots.');
  }

  const link = requireLink();
  const client = await getV2Client();
  const slug = await resolveOrgSlug(client, link);
  try {
    const result = await client.put<DevPolicyResponse>(
      `/api/v2/grids/${encodeURIComponent(slug)}/dev-sandbox/policy`,
      body,
    );
    renderPolicy(result.policy);
  } catch (err) {
    await exitForPolicyError(err);
  }
}

// ── Commander binding ────────────────────────────────────────────────────────

export const devCommand = new Command('dev')
  .description('Run the linked entity locally with grid-injected resources')
  .allowExcessArguments(true)
  .passThroughOptions(true)
  .option('--force', 'Ignore an existing .cloudgrid-dev.lock')
  .option('--fixed-ports', 'Refuse to fall back to ephemeral when previous ports are taken')
  .option('--status', 'Print the current dev-session lock contents (read-only)')
  .option('--kill', 'Kill any dev-session children recorded in the lock and remove the lock')
  .argument('[serviceOrCommand...]', 'Optional service name (multi-service mode) or command after --')
  .action(async (
    serviceOrCommand: string[],
    opts: { force?: boolean; fixedPorts?: boolean; status?: boolean; kill?: boolean },
  ) => {
    const start = Date.now();
    try {
      // Distinguishing between "single-service mode" and "escape hatch":
      //   - escape hatch: argv contains `--` before the user's command;
      //     commander strips `--` and passes args verbatim. We can detect
      //     escape-hatch by inspecting raw argv.
      //   - otherwise the first positional (if any) is a service name.
      const rawArgv = process.argv;
      const dashIdx = rawArgv.indexOf('--');
      const isEscapeHatch = dashIdx !== -1 && dashIdx < rawArgv.length - 1;

      const runOpts: RunDevOpts = {
        force: opts.force,
        fixedPorts: opts.fixedPorts,
        status: opts.status,
        kill: opts.kill,
      };
      if (isEscapeHatch) {
        runOpts.userCommand = rawArgv.slice(dashIdx + 1);
      } else if (serviceOrCommand.length === 1) {
        runOpts.serviceName = serviceOrCommand[0];
      } else if (serviceOrCommand.length > 1) {
        // No `--` but multiple positionals — the user likely meant the
        // escape hatch but forgot. Surface a helpful pointer rather than
        // guessing.
        throw new CloudGridError(
          'Pass a single service name (cloudgrid dev <name>) or use the escape hatch (cloudgrid dev -- <cmd ...>).',
        );
      }
      await runDev(runOpts);
      // Decision 016 — success event records the full dev-session duration
      // (start → user-Ctrl-C / clean child exit). Useful as an engagement
      // signal alongside init/plug startup metrics.
      try {
        const { recordCliEvent } = await import('../services/telemetry.js');
        await recordCliEvent({
          event_type: 'success',
          command: 'cloudgrid dev',
          elapsed_ms: Date.now() - start,
        });
      } catch { /* swallow */ }
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });

// Dev Grid M1 — `cloudgrid dev seed` and `cloudgrid dev reset`. The data mode
// defaults to the org default (currently `ai`); `policy` verbs ship in M2.
devCommand
  .command('seed')
  .description('Seed the grid sandbox on the Dev Grid')
  .option('--from <mode>', 'Data mode: live, snapshot, or ai (defaults to the grid default)')
  .option('--collections <names>', 'Comma-separated collection names to seed (cold start)')
  .option('--yes', 'Skip the confirmation prompt')
  .action(async (opts: { from?: string; collections?: string; yes?: boolean }) => {
    try {
      await runDevSeed(opts);
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });

devCommand
  .command('reset')
  .description('Drop the sandbox data and reseed to the grid default mode')
  .option('--yes', 'Skip the confirmation prompt')
  .action(async (opts: { yes?: boolean }) => {
    try {
      await runDevReset(opts);
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });

// Dev Grid M2 — `cloudgrid dev policy` (read, any member) and `dev policy set`
// (org admin). The default mode must be in the org's tier-allowed set.
const devPolicyCommand = devCommand
  .command('policy')
  .description("Show this grid's Dev Grid policy (default mode, allowed modes, snapshot state)")
  .option('--json', 'Print the policy as JSON')
  .action(async (opts: { json?: boolean }) => {
    try {
      await runDevPolicy(opts);
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });

devPolicyCommand
  .command('set')
  .description('Set the grid default mode or the snapshot opt-in (grid admin)')
  .option('--default-mode <mode>', 'Default data mode: live, snapshot, or ai')
  .option('--allow-snapshots <on|off>', 'Allow masked prod snapshots in the sandbox')
  .action(async (opts: { defaultMode?: string; allowSnapshots?: string }) => {
    try {
      await runDevPolicySet(opts);
    } catch (err) {
      await handleV2Error(err, { json: false });
    }
  });
